import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, catchError, defer, map, of, tap, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import { AssessmentTaskScopeChangesService } from "./assessment_task_scope_changes.service";
import { LoginService } from "./login.service";
import { assessmentTaskSessionIdentity } from "./assessment_task_session.util";
import { AssessmentAssignmentResult, AssessmentAssignmentRole, AssessmentInvitationPayload, InvitationResendResult,
    PersistedInvitationResult, StorageOnlyConversionResult, UserAccessCapabilities, UserManagementCapabilities, UserAccessContext, UserAccessQuery } from "./assessment_user_access.types";
import { accessId, assignmentRoles, deliveryErrorEnvelope, invitationRequest, normalizeAssignmentResult,
    normalizeInvitationError, normalizeInvitationResult, normalizeResendResult, normalizeUserAccess, normalizeUserAccessCapabilities,
    normalizeUserManagementCapabilities, normalizeUserAccessError, normalizeStorageOnlyConversion,
    UserAccessError } from "./assessment_user_access.util";

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

export interface AssessmentAssignment {
    displayName: string;
    productLicenseId: number;
    merchantId: number | null;
    merchantName: string;
    formType: string;
    featureSet: string | null;
    status: string | null;
    assignmentRoles: string[];
    assignmentLabels: string[];
}

export interface AccountUserProfile {
    id: number;
    accountId: number;
    displayName: string;
    email: string;
    role: string;
    assignedMerchantsForStorage?: number[];
    phoneNumber: string | null;
    certificateNumber: string | null;
    lastLoginAt: string | null;
    preferences: Record<string, unknown>;
    assessmentAssignments: AssessmentAssignment[];
    createdAt: string | null;
    updatedAt: string | null;
    capabilities?: UserManagementCapabilities;
}

export interface AccountProfile {
    id: number;
    name: string;
    legalName: string | null;
    dbaName: string | null;
    industry: string | null;
    website: string | null;
    mainPhone: string | null;
    supportEmail: string | null;
    timezone: string | null;
    countryCode: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    accountCode: string | null;
    docxGenerationEnabled?: boolean;
    storageAllowance: number;
    storageUsedBytes: number;
    aiTokenAllowance: number;
    aiInputTokens: number;
    aiOutputTokens: number;
    status: string | null;
    metadata: Record<string, unknown>;
    secureStorageConfig: SecureStorageConfiguration | null;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface SecureStorageMapping {
    provider: string | null;
    containerName: string | null;
    accountPathPrefix: string | null;
}

export interface SecureStorageConfiguration {
    enabled: boolean;
    isConfigured: boolean;
    locked: boolean;
    mapping: SecureStorageMapping | null;
    configuredAt: string | null;
    updatedAt: string | null;
}

export interface SecureStorageConfigurationUpdateResult {
    secureStorageConfig: SecureStorageConfiguration;
    storageAllowance: number;
}

export interface AccountMember {
    id: number;
    displayName: string;
    email: string;
    role: string;
    invitedByUserId?: number | null;
    invitedByDisplayName?: string | null;
    assignedMerchantsForStorage?: number[];
    phoneNumber: string | null;
    certificateNumber: string | null;
    lastLoginAt: string | null;
    assessmentAssignments: AssessmentAssignment[];
    createdAt: string | null;
    updatedAt: string | null;
    capabilities?: UserManagementCapabilities;
}

export interface AccountOverview {
    user: AccountUserProfile;
    account: AccountProfile | null;
    members: AccountMember[];
    inactiveMembers: AccountMember[];
    capabilities?: UserAccessCapabilities;
}

export interface NotificationEmailPreference {
    enabled: boolean;
}

export interface NotificationEmailPreferenceUpdate extends NotificationEmailPreference {
    changed: boolean;
}

export interface UpdateUserProfilePayload {
    userId?: number;
    displayName: string;
    phoneNumber?: string | null;
    certificateNumber?: string | null;
    role?: string;
}

export interface CreateStorageUserPayload {
    displayName: string;
    email: string;
    assignedMerchantIds: number[];
}

export interface UpdateStorageUserAssignedMerchantsPayload {
    assignedMerchantIds: number[];
}

@Injectable({ providedIn: "root" })
export class AccountSettingsService {
    private readonly apiBaseUrl = environment.baseurl.replace(/\/$/, "");
    private unresolvedInvitation: { owner: string; body: AssessmentInvitationPayload } | null = null;

    getUnresolvedInvitation(): AssessmentInvitationPayload | null {
        const owner = JSON.stringify([assessmentTaskSessionIdentity(this.login.getUserSnapshot()), this.login.getToken()]);
        if (this.unresolvedInvitation?.owner !== owner) this.unresolvedInvitation = null;
        const body = this.unresolvedInvitation?.body;
        return body ? { ...body, ...(body.desiredRoles ? { desiredRoles: [...body.desiredRoles] } : {}) } : null;
    }

    constructor(
        private readonly http: HttpClient,
        private readonly taskScopeChanges: AssessmentTaskScopeChangesService,
        private readonly login: LoginService
    ) {}

    getUserAccess(query: UserAccessQuery = {}): Observable<UserAccessContext> {
        return this.withUserAccessSession((accountId, actorUserId) => {
            const params: Record<string, string> = {};
            if (query.productLicenseId !== undefined) params.productLicenseId = String(accessId(query.productLicenseId));
            if (query.targetUserId !== undefined) params.targetUserId = String(accessId(query.targetUserId));
            const requested = { ...query };
            return this.http.get<unknown>(`${this.apiBaseUrl}/account-settings/user-access`, { params })
                .pipe(map((response) => normalizeUserAccess(response, accountId, actorUserId, requested)));
        });
    }

    setUserAssessmentAssignments(userId: number, productLicenseId: number,
        desiredRoles?: readonly AssessmentAssignmentRole[]): Observable<AssessmentAssignmentResult> {
        return this.withUserAccessSession((accountId) => {
            const target = accessId(userId), license = accessId(productLicenseId);
            // Omission means no write; an explicit empty array means remove all roles.
            const body = desiredRoles === undefined ? {} : { desiredRoles: assignmentRoles([...desiredRoles]) };
            return this.http.put<unknown>(`${this.apiBaseUrl}/account-settings/users/${target}/assessments/${license}/assignments`, body)
                .pipe(map((response) => normalizeAssignmentResult(response, accountId, license, target)));
        }, (result) => { if (result.changed) this.taskScopeChanges.assessmentChanged(result.productLicenseId); });
    }

    inviteUserWithAccess(payload: AssessmentInvitationPayload): Observable<PersistedInvitationResult> {
        return this.withUserAccessSession((accountId) => {
            const body = invitationRequest(payload);
            const previous = this.getUnresolvedInvitation();
            if (previous && JSON.stringify(previous) !== JSON.stringify(body)) {
                throw new UserAccessError("Resolve the previous invitation submission before creating another user.", "INVITATION_RESOLUTION_REQUIRED");
            }
            const attempt = { owner: JSON.stringify([assessmentTaskSessionIdentity(this.login.getUserSnapshot()), this.login.getToken()]), body };
            // Retain the exact intent even if the caller closes/unsubscribes
            // before receiving the transaction outcome. Never persist it in browser storage.
            this.unresolvedInvitation = attempt;
            return this.http.post<unknown>(`${this.apiBaseUrl}/account-settings/invite`, body).pipe(
                catchError((error: unknown) => {
                    const persisted = deliveryErrorEnvelope(error, ["INVITATION_DELIVERY_FAILED", "INVITATION_DELIVERY_UNKNOWN"]);
                    return persisted ? of(persisted) : throwError(() => error);
                }),
                map((response) => normalizeInvitationResult(response, accountId, body)),
                tap(() => { if (this.unresolvedInvitation === attempt) this.unresolvedInvitation = null; }),
                catchError((error: unknown) => {
                    const normalized = normalizeInvitationError(error, accountId, body.productLicenseId ?? null);
                    const rejection = ["ACCESS_DENIED", "INVALID_INVITATION", "PRIMARY_OCCUPIED", "INCOMPATIBLE_ROLES",
                        "INVALID_ASSIGNEE", "ASSIGNMENT_CONFLICT", "INVITATION_EXISTING_MEMBER", "INVITATION_EMAIL_CONFLICT",
                        "INVITATION_EXISTING_MEMBER_UNAVAILABLE", "INVITATION_SUBMISSION_CONFLICT", "INVITATION_OUTCOME_UNAVAILABLE",
                        "INVITE_EXISTING_STORAGE_ONLY_CONFLICT", "ASSESSMENT_REQUIRED", "ASSIGNMENT_REQUIRED"].includes(normalized.code);
                    const unresolved = previous && ["ACCESS_DENIED", "INVITATION_SUBMISSION_CONFLICT", "INVITATION_OUTCOME_UNAVAILABLE"].includes(normalized.code);
                    if (rejection && !unresolved && this.unresolvedInvitation === attempt) this.unresolvedInvitation = null;
                    return throwError(() => rejection ? normalized : new UserAccessError(
                        "The invitation submission outcome is not confirmed. Resolve this same submission before creating another user.",
                        "INVITATION_OUTCOME_UNKNOWN"));
                })
            );
        }, (result) => {
            // A receipt replay can be the first acknowledged response after a lost
            // commit response. Refresh its saved scope even though this request did
            // not repeat the assignment write.
            if (result.productLicenseId !== null && (result.assignmentChanged || result.duplicate)) {
                this.taskScopeChanges.assessmentChanged(result.productLicenseId);
            }
        });
    }

    resendUserInvitation(userId: number): Observable<InvitationResendResult> {
        return this.withUserAccessSession((accountId) => {
            const target = accessId(userId);
            return this.http.post<unknown>(`${this.apiBaseUrl}/account-settings/users/${target}/resend-invite`, {}).pipe(
                catchError((error: unknown) => {
                    const persisted = deliveryErrorEnvelope(error, ["RESEND_DELIVERY_FAILED"]);
                    return persisted ? of(persisted) : throwError(() => normalizeUserAccessError(error));
                }),
                map((response) => normalizeResendResult(response, accountId, target))
            );
        });
    }

    convertStorageOnlyInvitation(payload: AssessmentInvitationPayload, expectedUserId: number): Observable<StorageOnlyConversionResult> {
        return this.withUserAccessSession((accountId) => {
            const body = invitationRequest(payload), target = accessId(expectedUserId);
            if (body.productLicenseId !== undefined || body.assignExistingUserId !== undefined) {
                throw new UserAccessError("Storage Only conversion is available from User Settings only.", "ACCESS_DENIED");
            }
            // Preserve the existing server-authorized conversion endpoint and its semantics.
            return this.http.post<unknown>(`${this.apiBaseUrl}/account-settings/invite`, {
                ...body, convertExistingStorageOnlyUser: true
            }).pipe(map((response) => normalizeStorageOnlyConversion(response, accountId, target)));
        });
    }

    private withUserAccessSession<T>(request: (accountId: number, actorUserId: number) => Observable<T>,
        onCurrentResult?: (result: T) => void): Observable<T> {
        return defer(() => {
            const identity = assessmentTaskSessionIdentity(this.login.getUserSnapshot());
            const token = this.login.getToken();
            if (!identity || !token) throw new UserAccessError("Your session has changed. Please reopen user access.", "SESSION_CHANGED");
            const accountId = accessId(this.login.getCurrentAccountId());
            const actorUserId = accessId(this.login.getCurrentUserId());
            const assertCurrent = () => {
                if (identity !== assessmentTaskSessionIdentity(this.login.getUserSnapshot()) || token !== this.login.getToken()) {
                    throw new UserAccessError("Your session has changed. Please reopen user access.", "SESSION_CHANGED");
                }
            };
            return request(accountId, actorUserId).pipe(
                tap((result) => { assertCurrent(); onCurrentResult?.(result); }),
                catchError((error: unknown) => { assertCurrent(); return throwError(() => normalizeUserAccessError(error)); })
            );
        });
    }

    getOverview(): Observable<AccountOverview> {
        return this.http
            .get<ApiResponse<AccountOverview>>(`${this.apiBaseUrl}/account-settings`)
            .pipe(map((response) => this.extractOverview(response)));
    }

    getNotificationEmailPreference(): Observable<NotificationEmailPreference> {
        return this.http
            .get<ApiResponse<unknown>>(`${this.apiBaseUrl}/account-settings/notification-email`)
            .pipe(map((response) => this.extractNotificationEmailPreference(
                response,
                "Unable to load notification email settings."
            )));
    }

    updateNotificationEmailPreference(enabled: boolean): Observable<NotificationEmailPreferenceUpdate> {
        return this.http
            .patch<ApiResponse<unknown>>(`${this.apiBaseUrl}/account-settings/notification-email`, { enabled })
            .pipe(map((response) => {
                const preference = this.extractNotificationEmailPreference(
                    response,
                    "Unable to update notification email settings."
                );
                const changed = (response.data as Record<string, unknown>).changed;
                if (typeof changed !== "boolean") {
                    throw new Error("Unable to update notification email settings.");
                }
                return { ...preference, changed };
            }));
    }

    updateDisplayName(displayName: string): Observable<AccountUserProfile> {
        const payload = { displayName };
        return this.http
            .patch<ApiResponse<{ user: AccountUserProfile }>>(
                `${this.apiBaseUrl}/account-settings/display-name`,
                payload
            )
            .pipe(map((response) => this.extractUser(response)));
    }

    updateUserProfile(payload: UpdateUserProfilePayload): Observable<AccountUserProfile> {
        return this.http
            .patch<ApiResponse<{ user: AccountUserProfile }>>(
                `${this.apiBaseUrl}/account-settings/profile`,
                payload
            )
            .pipe(
                map((response) => this.extractUser(response)),
                tap(() => {
                    if (payload.role !== undefined) this.taskScopeChanges.accountChanged();
                })
            );
    }

    createStorageUser(payload: CreateStorageUserPayload): Observable<AccountUserProfile> {
        return this.http
            .post<ApiResponse<{ user: AccountUserProfile }>>(
                `${this.apiBaseUrl}/account-settings/storage-users`,
                payload
            )
            .pipe(map((response) => this.extractUser(response)));
    }

    updateStorageUserAssignedMerchants(
        userId: number,
        payload: UpdateStorageUserAssignedMerchantsPayload
    ): Observable<AccountUserProfile> {
        return this.http
            .patch<ApiResponse<{ user: AccountUserProfile }>>(
                `${this.apiBaseUrl}/account-settings/storage-users/${userId}/assigned-merchants`,
                payload
            )
            .pipe(map((response) => this.extractUser(response)));
    }

    updateSecureStorageConfiguration(enabled: boolean): Observable<SecureStorageConfigurationUpdateResult> {
        return this.http
            .patch<ApiResponse<{ secureStorageConfig: SecureStorageConfiguration; storageAllowance?: number }>>(
                `${this.apiBaseUrl}/account-settings/secure-storage`,
                { enabled }
            )
            .pipe(map((response) => this.extractSecureStorageConfigurationUpdate(response)));
    }

    initiatePasswordResetForUser(userId: number, email: string): Observable<void> {
        const payload = { userId, email };
        return this.http
            .post<ApiResponse<unknown>>(`${this.apiBaseUrl}/account-settings/users/${userId}/password-reset`, payload)
            .pipe(map((response) => this.ensureSuccess(response)));
    }

    initiatePasswordResetForStorageUser(userId: number, email: string): Observable<void> {
        const payload = { userId, email };
        return this.http
            .post<ApiResponse<unknown>>(
                `${this.apiBaseUrl}/account-settings/storage-users/${userId}/password-reset`,
                payload
            )
            .pipe(map((response) => this.ensureSuccess(response)));
    }

    changePassword(currentPassword: string, newPassword: string): Observable<void> {
        const payload = { currentPassword, newPassword };
        return this.http
            .post<ApiResponse<unknown>>(`${this.apiBaseUrl}/account-settings/change-password`, payload)
            .pipe(map((response) => this.ensureSuccess(response)));
    }

    deactivateAccount(): Observable<void> {
        return this.http
            .delete<ApiResponse<{ deactivated: boolean }>>(`${this.apiBaseUrl}/account-settings/account`)
            .pipe(
                map((response) => this.ensureSuccess(response)),
                tap(() => this.taskScopeChanges.accountChanged())
            );
    }

    deactivateUserAccount(userId: number): Observable<void> {
        return this.http
            .delete<ApiResponse<{ deactivated: boolean }>>(`${this.apiBaseUrl}/account-settings/users/${userId}`)
            .pipe(
                map((response) => this.ensureSuccess(response)),
                tap(() => this.taskScopeChanges.accountChanged())
            );
    }

    deactivateStorageUserAccount(userId: number): Observable<void> {
        return this.http
            .post<ApiResponse<{ deactivated: boolean }>>(
                `${this.apiBaseUrl}/account-settings/storage-users/${userId}/deactivate`,
                {}
            )
            .pipe(
                map((response) => this.ensureSuccess(response)),
                tap(() => this.taskScopeChanges.accountChanged())
            );
    }

    reactivateUserAccount(userId: number): Observable<void> {
        return this.http
            .post<ApiResponse<{ reactivated: boolean }>>(
                `${this.apiBaseUrl}/account-settings/users/${userId}/reactivate`,
                {}
            )
            .pipe(
                map((response) => this.ensureSuccess(response)),
                tap(() => this.taskScopeChanges.accountChanged())
            );
    }

    reactivateStorageUserAccount(userId: number): Observable<void> {
        return this.reactivateUserAccount(userId);
    }

    resendUserInvite(userId: number): Observable<AccountUserProfile> {
        return this.http
            .post<ApiResponse<{ user: AccountUserProfile; resent: boolean }>>(
                `${this.apiBaseUrl}/account-settings/users/${userId}/resend-invite`,
                {}
            )
            .pipe(map((response) => this.extractUser(response as ApiResponse<{ user: AccountUserProfile }>)));
    }

    deleteUserAccount(userId: number): Observable<void> {
        return this.http
            .delete<ApiResponse<{ deleted: boolean }>>(
                `${this.apiBaseUrl}/account-settings/users/${userId}/permanent`
            )
            .pipe(
                map((response) => this.ensureSuccess(response)),
                tap(() => this.taskScopeChanges.accountChanged())
            );
    }

    deleteStorageUserAccount(userId: number): Observable<void> {
        return this.http
            .delete<ApiResponse<{ deleted: boolean }>>(`${this.apiBaseUrl}/account-settings/storage-users/${userId}`)
            .pipe(
                map((response) => this.ensureSuccess(response)),
                tap(() => this.taskScopeChanges.accountChanged())
            );
    }

    private extractOverview(response: ApiResponse<AccountOverview>): AccountOverview {
        if (!response?.success || !response.data) {
            throw new Error(response?.message || "Unable to load account settings.");
        }

        return {
            ...(response.data.capabilities === undefined ? {} : { capabilities: normalizeUserAccessCapabilities(response.data.capabilities) }),
            user: this.normalizeUser(response.data.user),
            account: response.data.account ? this.normalizeAccount(response.data.account) : null,
            members: Array.isArray(response.data.members)
                ? response.data.members.map((member) => this.normalizeMember(member))
                : [],
            inactiveMembers: Array.isArray((response.data as { inactiveMembers?: AccountMember[] }).inactiveMembers)
                ? (response.data as { inactiveMembers?: AccountMember[] }).inactiveMembers!.map((member) =>
                      this.normalizeMember(member)
                  )
                : []
        };
    }

    private extractUser(response: ApiResponse<{ user: AccountUserProfile }>): AccountUserProfile {
        if (!response?.success || !response.data?.user) {
            throw new Error(response?.message || "Unable to update user profile.");
        }

        return this.normalizeUser(response.data.user);
    }

    private ensureSuccess(response: ApiResponse<any>): void {
        if (!response?.success) {
            throw new Error(response?.message || "Request failed.");
        }
    }

    private extractSecureStorageConfigurationUpdate(
        response: ApiResponse<{ secureStorageConfig: SecureStorageConfiguration; storageAllowance?: number }>
    ): SecureStorageConfigurationUpdateResult {
        if (!response?.success || !response.data?.secureStorageConfig) {
            throw new Error(response?.message || "Unable to update Secure Storage configuration.");
        }

        const normalizedConfiguration = this.normalizeSecureStorageConfiguration(response.data.secureStorageConfig);
        const hasExplicitStorageAllowance =
            response.data.storageAllowance !== null && response.data.storageAllowance !== undefined;
        return {
            secureStorageConfig: normalizedConfiguration,
            // Keep backend as source of truth when provided; otherwise fall back to enabled-state semantics.
            storageAllowance: hasExplicitStorageAllowance
                ? this.toNumber(response.data.storageAllowance)
                : normalizedConfiguration.enabled
                    ? 100
                    : 0
        };
    }

    private normalizeUser(value: any): AccountUserProfile {
        return {
            ...(value?.capabilities === undefined ? {} : { capabilities: normalizeUserManagementCapabilities(value.capabilities) }),
            id: this.toNumber(value?.id),
            accountId: this.toNumber(value?.accountId ?? value?.account_id),
            displayName: this.toString(value?.displayName ?? value?.display_name),
            email: this.toString(value?.email),
            role: this.toString(value?.role),
            assignedMerchantsForStorage: this.toNumberArray(
                value?.assignedMerchantsForStorage ?? value?.assigned_merchants_for_storage
            ),
            phoneNumber: this.toOptionalString(value?.phoneNumber ?? value?.phone_number),
            certificateNumber: this.toOptionalString(
                value?.certificateNumber ?? value?.certificate_number
            ),
            lastLoginAt: this.toNullableString(value?.lastLoginAt ?? value?.last_login_at),
            preferences: this.toObject(value?.preferences),
            assessmentAssignments: this.toAssessmentAssignments(
                value?.assessmentAssignments ?? value?.assessment_assignments
            ),
            createdAt: this.toNullableString(value?.createdAt ?? value?.created_at),
            updatedAt: this.toNullableString(value?.updatedAt ?? value?.updated_at)
        };
    }

    private normalizeAccount(value: any): AccountProfile {
        return {
            id: this.toNumber(value?.id),
            name: this.toString(value?.name),
            legalName: this.toOptionalString(value?.legalName ?? value?.legal_name),
            dbaName: this.toOptionalString(value?.dbaName ?? value?.dba_name),
            industry: this.toOptionalString(value?.industry),
            website: this.toOptionalString(value?.website),
            mainPhone: this.toOptionalString(value?.mainPhone ?? value?.main_phone),
            supportEmail: this.toOptionalString(value?.supportEmail ?? value?.support_email),
            timezone: this.toOptionalString(value?.timezone),
            countryCode: this.toOptionalString(value?.countryCode ?? value?.country_code),
            addressLine1: this.toOptionalString(value?.addressLine1 ?? value?.address_line1),
            addressLine2: this.toOptionalString(value?.addressLine2 ?? value?.address_line2),
            city: this.toOptionalString(value?.city),
            region: this.toOptionalString(value?.region),
            postalCode: this.toOptionalString(value?.postalCode ?? value?.postal_code),
            accountCode: this.toOptionalString(value?.accountCode ?? value?.account_code),
            docxGenerationEnabled: value?.docxGenerationEnabled === true || value?.docx_generation_enabled === 1,
            storageAllowance: this.toNumber(value?.storageAllowance ?? value?.storage_allowance),
            storageUsedBytes: this.toNumber(value?.storageUsedBytes ?? value?.storage_used_bytes),
            aiTokenAllowance: this.toNumber(value?.aiTokenAllowance ?? value?.ai_token_allowance),
            aiInputTokens: this.toNumber(value?.aiInputTokens ?? value?.ai_input_tokens),
            aiOutputTokens: this.toNumber(value?.aiOutputTokens ?? value?.ai_output_tokens),
            status: this.toOptionalString(value?.status),
            metadata: this.toObject(value?.metadata),
            secureStorageConfig: this.normalizeSecureStorageConfiguration(
                value?.secureStorageConfig ?? value?.secure_storage_config
            ),
            createdAt: this.toNullableString(value?.createdAt ?? value?.created_at),
            updatedAt: this.toNullableString(value?.updatedAt ?? value?.updated_at)
        };
    }

    private normalizeMember(value: any): AccountMember {
        return {
            ...(value?.capabilities === undefined ? {} : { capabilities: normalizeUserManagementCapabilities(value.capabilities) }),
            id: this.toNumber(value?.id),
            displayName: this.toString(value?.displayName ?? value?.display_name),
            email: this.toString(value?.email),
            role: this.toString(value?.role),
            invitedByUserId: this.toNullableNumber(value?.invitedByUserId ?? value?.invited_by_user_id),
            invitedByDisplayName: this.toOptionalString(value?.invitedByDisplayName ?? value?.invited_by_display_name),
            assignedMerchantsForStorage: this.toNumberArray(
                value?.assignedMerchantsForStorage ?? value?.assigned_merchants_for_storage
            ),
            phoneNumber: this.toOptionalString(value?.phoneNumber ?? value?.phone_number),
            certificateNumber: this.toOptionalString(
                value?.certificateNumber ?? value?.certificate_number
            ),
            lastLoginAt: this.toNullableString(value?.lastLoginAt ?? value?.last_login_at),
            assessmentAssignments: this.toAssessmentAssignments(
                value?.assessmentAssignments ?? value?.assessment_assignments
            ),
            createdAt: this.toNullableString(value?.createdAt ?? value?.created_at),
            updatedAt: this.toNullableString(value?.updatedAt ?? value?.updated_at)
        };
    }

    private toAssessmentAssignments(value: unknown): AssessmentAssignment[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return value
            .map((entry) => this.normalizeAssessmentAssignment(entry))
            .filter((entry) => entry.productLicenseId > 0);
    }

    private normalizeSecureStorageConfiguration(value: unknown): SecureStorageConfiguration | null {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
            return null;
        }

        const source = value as Record<string, unknown>;
        const normalizedMapping = this.normalizeSecureStorageMapping(source.mapping);
        return {
            enabled: this.toBoolean(source.enabled),
            isConfigured: this.toBoolean(source.isConfigured ?? source.is_configured),
            locked: this.toBoolean(source.locked),
            mapping: normalizedMapping,
            configuredAt: this.toNullableString(source.configuredAt ?? source.configured_at),
            updatedAt: this.toNullableString(source.updatedAt ?? source.updated_at)
        };
    }

    private extractNotificationEmailPreference(
        response: ApiResponse<unknown>,
        errorMessage: string
    ): NotificationEmailPreference {
        if (response?.success !== true) {
            throw new Error(response?.message || errorMessage);
        }
        const data = response.data;
        if (!data || typeof data !== "object" || Array.isArray(data)) {
            throw new Error(errorMessage);
        }
        const enabled = (data as Record<string, unknown>).enabled;
        if (typeof enabled !== "boolean") {
            throw new Error(errorMessage);
        }
        return { enabled };
    }

    private normalizeSecureStorageMapping(value: unknown): SecureStorageMapping | null {
        if (!value || typeof value !== "object" || Array.isArray(value)) {
            return null;
        }

        const source = value as Record<string, unknown>;
        return {
            provider: this.toOptionalString(source.provider),
            containerName: this.toOptionalString(source.containerName ?? source.container_name),
            accountPathPrefix: this.toOptionalString(source.accountPathPrefix ?? source.account_path_prefix)
        };
    }

    private normalizeAssessmentAssignment(value: any): AssessmentAssignment {
        return {
            displayName: this.toString(value?.displayName ?? value?.display_name) || "Assessment",
            productLicenseId: this.toNumber(value?.productLicenseId ?? value?.product_license_id ?? value?.id),
            merchantId: this.toNullableNumber(value?.merchantId ?? value?.merchant_id),
            merchantName: this.toString(value?.merchantName ?? value?.merchant_name),
            formType: this.toString(value?.formType ?? value?.form_type),
            featureSet: this.toOptionalString(value?.featureSet ?? value?.feature_set),
            status: this.toOptionalString(value?.status),
            assignmentRoles: this.toUniqueStringArray(value?.assignmentRoles ?? value?.assignment_roles),
            assignmentLabels: this.toUniqueStringArray(value?.assignmentLabels ?? value?.assignment_labels)
        };
    }

    private toNumber(value: unknown): number {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
    }

    private toBoolean(value: unknown): boolean {
        if (value === true || value === 1) {
            return true;
        }

        if (typeof value === "string") {
            const normalized = value.trim().toLowerCase();
            return normalized === "true" || normalized === "1" || normalized === "yes";
        }

        return false;
    }

    private toString(value: unknown): string {
        if (typeof value === "string") {
            return value;
        }
        if (value === null || value === undefined) {
            return "";
        }
        return String(value);
    }

    private toOptionalString(value: unknown): string | null {
        const normalized = this.toString(value).trim();
        return normalized ? normalized : null;
    }

    private toNullableNumber(value: unknown): number | null {
        const num = Number(value);
        return Number.isFinite(num) && num > 0 ? num : null;
    }

    private toNullableString(value: unknown): string | null {
        if (!value) {
            return null;
        }
        const str = this.toString(value).trim();
        return str || null;
    }

    private toObject(value: unknown): Record<string, unknown> {
        if (!value) {
            return {};
        }
        if (typeof value === "object" && !Array.isArray(value)) {
            return value as Record<string, unknown>;
        }
        try {
            const parsed = JSON.parse(String(value));
            return typeof parsed === "object" && parsed ? (parsed as Record<string, unknown>) : {};
        } catch (error) {
            return {};
        }
    }

    private toUniqueStringArray(value: unknown): string[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return [...new Set(
            value
                .map((entry) => this.toString(entry).trim())
                .filter((entry) => entry.length > 0)
        )];
    }

    private toNumberArray(value: unknown): number[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return [...new Set(
            value
                .map((entry) => Number(entry))
                .filter((entry) => Number.isInteger(entry) && entry > 0)
        )];
    }
}
